import React from "react";
import { Boxes, Store, Truck, Database, MapPin } from "lucide-react";
import { KIND_LABEL, isSample, plainName } from "../../lib/searchApi";
import { formatNumber, formatPeso, cn } from "../../lib/utils";

const ICON = { sku: Boxes, branch: Store, supplier: Truck };

const fmt = (c) => {
  if (c.unit === "php") return formatPeso(c.value, { compact: true });
  if (c.unit === "pct") return `${formatNumber(c.value, { decimals: 1 })}%`;
  if (c.unit === "days") return `${formatNumber(c.value, { decimals: c.value < 10 ? 1 : 0 })} d`;
  return formatNumber(c.value, { compact: c.value >= 100000 });
};

export function EntityHead({ entity, name, facts }) {
  const Icon = ICON[entity.kind] || Boxes;
  const layers = [...new Set((facts?.sources || []).map(s => s.layer))];
  return (
    <div data-testid="ai-search-entity" className="flex items-start gap-3">
      <div className="w-10 h-10 shrink-0 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><Icon size={18} /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted font-semibold">{KIND_LABEL[entity.kind]}</div>
        <div className="font-display text-[17px] font-semibold leading-tight break-all" data-testid="ai-search-entity-code">{entity.code}</div>
        {name && <div className="text-[12.5px] text-muted" data-testid="ai-search-entity-name">{plainName(name)}</div>}
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {isSample(name) && (
            <span data-testid="ai-search-sample-name" title="Placeholder name until the real master list is uploaded"
                  className="text-[9.5px] uppercase tracking-widest font-semibold border border-warning/40 bg-warning/10 text-warning rounded-sm px-1.5 py-0.5">Sample name</span>
          )}
          {layers.map(l => (
            <span key={l} data-testid={`ai-search-layer-${l}`} className="text-[9.5px] uppercase tracking-widest font-semibold border border-border bg-surface-2 rounded-sm px-1.5 py-0.5">{l}</span>
          ))}
          {facts?.branch_scope && (
            <span data-testid="ai-search-branch-scope" className="text-[9.5px] uppercase tracking-widest font-semibold border border-primary/30 text-primary rounded-sm px-1.5 py-0.5 inline-flex items-center gap-1">
              <MapPin size={9} /> {facts.branch_scope} only
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function FactCards({ cards }) {
  if (!cards?.length) return null;
  const n = cards.length;
  const three = n % 3 === 0 || n === 5;
  const span = (i) => cn(i === n - 1 && n % 2 === 1 && "col-span-2",
    three ? (n === 5 && i === 4 ? "sm:col-span-2" : "sm:col-span-1") : (i === n - 1 && n % 2 === 1 && "sm:col-span-2"));
  return (
    <div data-testid="ai-search-cards" className={cn("grid grid-cols-2 gap-px bg-border border border-border rounded-sm overflow-hidden", three && "sm:grid-cols-3")}>
      {cards.map((c, i) => (
        <div key={c.label} data-testid={`ai-search-card-${i}`} className={cn("bg-surface px-3 py-2.5", span(i))}>
          <div className="kpi-label text-[10px]">{c.label}</div>
          <div className="kpi-value text-lg mt-0.5">{fmt(c)}</div>
          {c.hint && <div className="text-[10.5px] text-muted mt-0.5 leading-snug">{c.hint}</div>}
        </div>
      ))}
    </div>
  );
}

export function OtherMatches({ matches, q, onSearch }) {
  return (
    <div>
      <div className="kpi-label text-[10px] mb-2">Other matches</div>
      <div className="flex flex-wrap gap-1.5" data-testid="ai-search-other-matches">
        {matches.map(m => (
          <button key={`${m.kind}-${m.code}`} type="button" data-testid={`ai-search-match-${m.kind}-${m.code}`}
                  onClick={() => onSearch({ q: m.code, kind: m.kind, code: m.code })}
                  className="text-[11.5px] px-2 py-1 border border-border rounded-sm hover:border-primary hover:text-primary transition-colors">
            <span className="text-subtle mr-1">{KIND_LABEL[m.kind]}</span><span className="font-mono-tabular">{m.code}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function DataUsed({ facts }) {
  return (
    <div data-testid="ai-search-data-used" className="text-[11.5px] text-muted border border-dashed border-border rounded-sm px-3.5 py-3">
      <div className="kpi-label text-[10px] mb-1.5 inline-flex items-center gap-1.5"><Database size={10} /> Data used</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {(facts.sources || []).map(s => <span key={s.type}>{s.label} <span className="uppercase text-[9.5px] tracking-wider text-subtle">{s.layer}</span></span>)}
      </div>
      {facts.missing_tables?.length > 0 && (
        <div className="mt-1.5" data-testid="ai-search-missing">Not promoted yet: {facts.missing_tables.map(m => m.label).join(", ")}</div>
      )}
    </div>
  );
}
